import { useState } from "react";
import { api } from "../../services/api/endpoints";
import type { TierId } from "../../services/api/schemas";

/**
 * Opens the Stripe billing portal — cancel, update a card, get an invoice.
 *
 * ── THIS IS THE CANCEL PATH, AND THERE HAS NEVER BEEN ANOTHER ────────────────
 * Until 2026-08-29 the product had no way to cancel at all. Account.tsx told
 * subscribers to "use the billing portal from your plan above" and the Terms
 * promised cancellation any time, while the API served no portal route and this
 * directory had no call for one. Both statements described a control nobody had
 * built. If this hook is ever removed, those two pieces of copy have to go with
 * it or the product goes back to promising something it does not have.
 *
 * ── IT DOES CHANGE PLANS NOW, AND THE REASON IT COULD NOT IS GONE ───────────
 * This block used to forbid plan changes here, because `_apply_plan_change`
 * attributed a change from the subscription's METADATA and only `_upgrade`
 * rewrote that metadata — so a swap made inside the portal would bill the new
 * plan and grant the old one.
 *
 * That was fixed server-side on 2026-08-29, the same day the warning was
 * written: `_apply_plan_change` now derives the plan from the PRICE on the
 * subscription — the fact the customer's card is actually charged against — and
 * falls back to metadata only for a price the environment cannot resolve. A
 * portal-initiated change is therefore attributed correctly. The warning
 * outlived the problem by about a day, which is the argument for rewriting it
 * rather than deleting it: "we deliberately do not do X" is exactly the kind of
 * note that goes on being believed long after it stopped being true.
 *
 * ⚠️ WHAT IS STILL REQUIRED IS A DASHBOARD SETTING. `subscription_update` must
 * be enabled on the Stripe portal configuration with the products listed, or the
 * update flow is refused. Test and live are configured SEPARATELY, so this can
 * work all the way through dev and fail for the first real customer — the same
 * trap as the portal configuration itself, one setting deeper.
 *
 * ── WHY IT IS A FULL PAGE LEAVE ─────────────────────────────────────────────
 * Same rule useCheckout documents, for the same reason: the URL is Stripe's and
 * cross-origin, so routing to it is impossible, and `location.assign()` is what
 * proves session restore works on the way back. Do not "optimise" it into a
 * client-side navigation — the mock would pass and production would log the
 * customer out in the middle of cancelling.
 *
 * ── THE ARGUMENT CHOOSES A DOOR, NEVER A CUSTOMER ───────────────────────────
 * The server reads the Stripe customer AND the subscription off the caller's own
 * rows. Nothing identifying either travels on this wire in either direction, so
 * a signed-in user cannot open a portal over someone else's card — and the
 * `intent` does not change that. It selects which of the portal's pages to land
 * on and, for an update, which price to put in front of them; that price is
 * cross-checked against the grant map server-side, so it cannot name a plan the
 * caller is not being sold. A customer or subscription id here would be the
 * first step to breaking the property, and there is still nowhere to put one.
 *
 * ── THE ERROR COPY, AND WHY IT IS NOT "TRY AGAIN" ───────────────────────────
 * The likely failure is not transient. `portal_not_configured` means the Stripe
 * account has no portal configuration — configured separately in test and live
 * mode, so it can work all the way through dev and fail for the first real
 * customer. Telling that person to try again would be advice that cannot
 * possibly work, on the screen where they are trying to stop paying us. The
 * message points at support instead, which is the only thing that helps.
 */
/** What door of the portal to open. `undefined` is the front page. */
export type PortalIntent =
  | { intent: "update"; priceId: string; grants: { kind: "tier"; tier: TierId } }
  | { intent: "cancel" };

export function useBillingPortal() {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Which button is busy, so a page with three of them can disable only the one
     that was pressed. `opening` alone would grey out all of them, which reads as
     the page having locked up rather than a click being in flight. */
  const [pending, setPending] = useState<string | null>(null);

  /** Resolves TRUE if the customer is on their way to Stripe, FALSE if not.
   *
   * The boolean exists so a caller can FALL BACK rather than dead-end. The
   * update flow needs `subscription_update` enabled on the Stripe portal
   * configuration, which is a dashboard setting and separate per mode — so
   * "the customer cannot change plan right now" is a state this can genuinely
   * be in, through no fault of theirs. Pricing uses the in-app upgrade path
   * when this says false, which keeps upgrades working while the setting
   * catches up. Cancellation has no such fallback and does not need one: the
   * portal's front page has always offered it.
   */
  async function open(input?: PortalIntent,
                      opts?: { quiet?: boolean }): Promise<boolean> {
    setOpening(true);
    setPending(input?.intent === "update" ? input.grants.tier : (input?.intent ?? "portal"));
    setError(null);
    try {
      const { url } = await api.billingPortal(input);
      window.location.assign(url);
      /* No setOpening(false) on success, deliberately — the page is leaving, and
         flipping the button back to its resting label mid-navigation reads as
         the click having failed. useCheckout makes the same choice. */
      return true;
    } catch {
      /* `quiet` is for a caller that has somewhere else to go. Announcing a
         failure it is about to recover from would tell the customer something
         broke and then work anyway — the Pricing page falls back to the in-app
         upgrade, so the portal being unavailable is not yet a failure THERE.
         Default is loud, which is what Account needs: it has no fallback, and a
         silent dead end on the cancel button is the worst screen in the product
         to be quiet on. */
      if (!opts?.quiet) reportFailure();
      setOpening(false);
      setPending(null);
      return false;
    }
  }

  /** Say the error out loud. Callable on its own so a `quiet` caller can decide
   *  later that it really has run out of options. */
  function reportFailure() {
    setError("Couldn't open billing just now. Email support and we'll sort it out.");
  }

  return { open, opening, pending, error, reportFailure };
}
